import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, CheckCircle2 } from "lucide-react";
import { api, apiError } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters");
    if (password !== confirm) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-black pt-28 pb-20 px-4 flex items-start justify-center">
      <div className="bg-white rounded-3xl shadow-2xl p-8 sm:p-10 w-full max-w-md mt-10">
        {done ? (
          <div className="text-center" data-testid="reset-success">
            <div className="mx-auto w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-5">
              <CheckCircle2 size={34} className="text-emerald-500" />
            </div>
            <h1 className="font-display text-2xl font-extrabold text-slate-900 mb-2">Password updated</h1>
            <p className="text-sm text-slate-500 mb-6">You can now sign in with your new password.</p>
            <Link to="/admin" data-testid="reset-goto-login" className="btn-ocean inline-block text-white font-bold px-8 py-3 rounded-full">
              Go to Sign In
            </Link>
          </div>
        ) : !token ? (
          <p className="text-sm text-slate-600">This reset link is invalid. Please request a new one from the login page.</p>
        ) : (
          <form onSubmit={submit}>
            <div className="flex items-center gap-2 mb-1">
              <KeyRound size={22} className="text-sky-600" />
              <h1 className="font-display text-2xl font-extrabold text-slate-900">Reset Password</h1>
            </div>
            <p className="text-sm text-slate-500 mb-6">Choose a new password (min 8 characters).</p>
            <input
              data-testid="reset-password-input"
              type="password"
              className="w-full border border-slate-300 rounded-xl px-4 py-3 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-sky-400"
              placeholder="New password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <input
              data-testid="reset-confirm-input"
              type="password"
              className="w-full border border-slate-300 rounded-xl px-4 py-3 text-sm mb-5 focus:outline-none focus:ring-2 focus:ring-sky-400"
              placeholder="Confirm new password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
            <button data-testid="reset-submit-button" type="submit" disabled={busy} className="btn-ocean w-full text-white font-bold py-3 rounded-full disabled:opacity-60">
              {busy ? "Updating…" : "Update Password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
